// /Users/pramod/Desktop/school-erp/frontend/src/Parent/components/CalendarView.js

import React, { useState } from 'react';

const CalendarView = ({ 
  events = [], 
  groupedEvents = {}, 
  eventTypes = [], 
  totalEvents = 0,
  todayEvents = [], 
  upcomingEvents = [] 
}) => {
  const [viewMode, setViewMode] = useState('all');

  const getEventTypeColor = (type) => {
    const map = {
      academic: 'bg-indigo-100 text-indigo-700',
      exam: 'bg-amber-100 text-amber-700',
      holiday: 'bg-rose-100 text-rose-700',
      cultural: 'bg-purple-100 text-purple-700',
    };
    return map[type?.toLowerCase()] || 'bg-gray-100 text-gray-500';
  };

  const getEventTypeLabel = (type) => {
    const map = {
      academic: 'Academic',
      exam: 'Exam',
      holiday: 'Holiday',
      cultural: 'Cultural',
    };
    return map[type?.toLowerCase()] || type || 'Event';
  };

  const getEventTypeIcon = (type) => {
    const map = {
      academic: '📚',
      exam: '📝',
      holiday: '🎉',
      cultural: '🎭',
    };
    return map[type?.toLowerCase()] || '📌';
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDisplayEvents = () => {
    if (viewMode === 'today') return todayEvents;
    if (viewMode === 'upcoming') return upcomingEvents;
    return events;
  };

  const displayEvents = getDisplayEvents();

  return (
    <div className="space-y-6">
      {/* View Toggle */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
        <button
          onClick={() => setViewMode('all')}
          className={`px-4 py-2 text-sm font-bold rounded-lg transition-all active:scale-95 ${
            viewMode === 'all' 
              ? 'bg-white text-slate-900 shadow-sm border border-slate-200/30' 
              : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          📅 All Events
        </button>
        <button
          onClick={() => setViewMode('today')}
          className={`px-4 py-2 text-sm font-bold rounded-lg transition-all active:scale-95 ${
            viewMode === 'today' 
              ? 'bg-white text-slate-900 shadow-sm border border-slate-200/30' 
              : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          📌 Today
        </button>
        <button
          onClick={() => setViewMode('upcoming')}
          className={`px-4 py-2 text-sm font-bold rounded-lg transition-all active:scale-95 ${
            viewMode === 'upcoming' 
              ? 'bg-white text-slate-900 shadow-sm border border-slate-200/30' 
              : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          🔮 Upcoming
        </button>
      </div>

      {/* Total Count */}
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-slate-400">
          {viewMode === 'all' 
            ? `${totalEvents} events total` 
            : `${displayEvents.length} events`}
        </p>
        {eventTypes.length > 0 && (
          <div className="flex gap-1.5 flex-wrap">
            {eventTypes.slice(0, 3).map((type) => (
              <span key={type} className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${getEventTypeColor(type)}`}>
                {getEventTypeIcon(type)} {getEventTypeLabel(type)}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* All Events - Grouped by Month */}
      {viewMode === 'all' && (
        <div className="space-y-6">
          {Object.keys(groupedEvents).sort().length > 0 ? (
            Object.keys(groupedEvents).sort().map((monthKey) => {
              const monthEvents = groupedEvents[monthKey];
              const [year, month] = monthKey.split('-');
              const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { month: 'long' });
              
              return (
                <div key={monthKey}>
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-sm font-bold text-slate-700">
                      {monthName} {year}
                    </h3>
                    <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                      {monthEvents.length} events
                    </span>
                  </div>
                  <div className="space-y-2">
                    {monthEvents.map((event, index) => (
                      <div
                        key={index}
                        className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-slate-50 rounded-xl border border-slate-100 hover:border-slate-200 transition-colors"
                      >
                        <div className="min-w-[100px]">
                          <p className="text-xs font-bold text-slate-700">{formatDate(event.startDate)}</p>
                          <p className="text-[10px] text-slate-400">
                            {event.isAllDay ? 'All Day' : `${event.startTime || ''} - ${event.endTime || ''}`}
                          </p>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-slate-800 truncate">{event.eventName}</p>
                          {event.description && (
                            <p className="text-xs text-slate-400 truncate">{event.description}</p>
                          )}
                        </div>
                        <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${getEventTypeColor(event.eventType)}`}>
                          {getEventTypeIcon(event.eventType)} {getEventTypeLabel(event.eventType)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="bg-slate-50 rounded-2xl p-8 text-center border border-slate-100">
              <div className="text-4xl mb-2">📅</div>
              <p className="text-slate-400 text-sm font-medium">No events found for this academic year.</p>
            </div>
          )}
        </div>
      )}

      {/* Today Events */}
      {viewMode === 'today' && (
        <div className="space-y-2">
          {todayEvents.length === 0 ? (
            <div className="bg-slate-50 rounded-2xl p-8 text-center border border-slate-100">
              <div className="text-4xl mb-2">📌</div>
              <p className="text-slate-400 text-sm font-medium">No events scheduled for today.</p>
            </div>
          ) : (
            todayEvents.map((event, index) => (
              <div
                key={index}
                className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-slate-50 rounded-xl border border-slate-100"
              >
                <div className="min-w-[100px]">
                  <p className="text-xs font-bold text-slate-700">{formatDate(event.startDate)}</p>
                  <p className="text-[10px] text-slate-400">
                    {event.isAllDay ? 'All Day' : `${event.startTime || ''} - ${event.endTime || ''}`}
                  </p>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-800">{event.eventName}</p>
                  {event.description && (
                    <p className="text-xs text-slate-400">{event.description}</p>
                  )}
                </div>
                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${getEventTypeColor(event.eventType)}`}>
                  {getEventTypeIcon(event.eventType)} {getEventTypeLabel(event.eventType)}
                </span>
              </div>
            ))
          )}
        </div>
      )}

      {/* Upcoming Events */}
      {viewMode === 'upcoming' && (
        <div className="space-y-2">
          {upcomingEvents.length === 0 ? (
            <div className="bg-slate-50 rounded-2xl p-8 text-center border border-slate-100">
              <div className="text-4xl mb-2">🔮</div>
              <p className="text-slate-400 text-sm font-medium">No upcoming events in the next 7 days.</p>
            </div>
          ) : (
            upcomingEvents.map((event, index) => (
              <div
                key={index}
                className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-slate-50 rounded-xl border border-slate-100 hover:border-slate-200 transition-colors"
              >
                <div className="min-w-[100px]">
                  <p className="text-xs font-bold text-slate-700">{formatDate(event.startDate)}</p>
                  <p className="text-[10px] text-slate-400">
                    {event.isAllDay ? 'All Day' : `${event.startTime || ''} - ${event.endTime || ''}`}
                  </p>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-800">{event.eventName}</p>
                  {event.description && (
                    <p className="text-xs text-slate-400">{event.description}</p>
                  )}
                </div>
                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${getEventTypeColor(event.eventType)}`}>
                  {getEventTypeIcon(event.eventType)} {getEventTypeLabel(event.eventType)}
                </span>
              </div>
            ))
          )}
        </div>
      )}

      {/* Legend */}
      <div className="pt-4 border-t border-slate-100">
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Event Types</p>
        <div className="flex flex-wrap gap-2">
          {['academic', 'exam', 'holiday', 'cultural'].map((type) => (
            <span
              key={type}
              className={`px-3 py-1 rounded-full text-xs font-bold ${getEventTypeColor(type)}`}
            >
              {getEventTypeIcon(type)} {getEventTypeLabel(type)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CalendarView;